import { getId } from '@/assets/js/utils';
import DocumentType from '@/assets/js/enum/DocumentType';
import { xhr } from '@/xhr/endpoints';
import { getOutlineNodeForDisplay, getOutlineNodeForTransfer } from '../../assets/js/utils';
import { fixedRowHeight } from '@/assets/js/constants';
import RoundStatus from '@/assets/js/enum/RoundStatus';
//
// state
const state = {
  hoverOutlineNodes: null,
  nodeId: {},

  showingOutlineNodeCitationsMap: {},
  basisEdited: false,
};

//
// getters
const getters = {
  hoverOutlineNodesIds: state =>
    state.hoverOutlineNodes ? state.hoverOutlineNodes.map(n => n._id) : [],

  /**
   * Returns an array with the outline node an its children flattened
   */
  flatOutlineNodes: (state, getters) => outlineNodes => {
    return outlineNodes?.reduce(
      (acc, outlineNode) => [
        ...acc,
        outlineNode,
        ...(outlineNode.children ? getters.flatOutlineNodes(outlineNode.children) : []),
      ],
      []
    );
  },

  getVisibleOutlineNodes: (state, getters) => outlineNodes => {
    return outlineNodes?.reduce((acc, outlineNode) => {
      if (outlineNode.level === 1) {
        if (outlineNode.open)
          return [
            ...acc,
            outlineNode,
            ...(outlineNode.children ? getters.getVisibleOutlineNodes(outlineNode.children) : []),
          ];
        else return [...acc, outlineNode];
      }
      if (outlineNode.children && outlineNode.open)
        return [...acc, outlineNode, ...getters.getVisibleOutlineNodes(outlineNode.children)];
      if (!outlineNode.children) return [...acc, outlineNode];
      return acc;
    }, []);
  },

  outlineInnerTextNodes: (state, getters) => outlineNode => {
    return outlineNode.sublevel
      ? [
          ...outlineNode.sublevel.flatMap(sublevel => getters.outlineInnerTextNodes(sublevel)),
          ...(outlineNode.nodes || outlineNode.basisNodes || outlineNode.textNodes),
        ]
      : outlineNode.nodes || outlineNode.basisNodes || outlineNode.textNodes;
  },

  outline: (state, getters) => {
    if (
      [DocumentType.ARTICLE, DocumentType.TEXT].includes(getters.documentSelectedSource.sourceType)
    ) {
      let data = getters.nlpOutline;
      return (
        (data?.outline?.outline ? data?.outline.outline : data?.outline).map(node =>
          getOutlineNodeForDisplay(node)
        ) || []
      );
    }
    if (getters.documentSelectedSource.sourceType === DocumentType.OUTLINE) {
      return getters.documentSelectedSource.outline.map(node => getOutlineNodeForDisplay(node));
    }

    return [];
  },
  // ***
  nlpOutline: state => {
    let AIOutline = state.documentData?.sources?.find(
      source => source.sourceType === DocumentType.OUTLINE && source.outlineType === 'AI-Edited'
    );
    if (!AIOutline)
      AIOutline = state.documentData?.sources?.find(
        source => source.sourceType === DocumentType.OUTLINE && source.outlineType === 'AI-Outline'
      );
    return AIOutline;
  },

  outlineTextNodeToOutlineNodeMapping: (state, getters) => {
    const outlineTextNodeToOutlineNodeMapping = Object.entries(getters.flatOutlineTextNodes).reduce(
      (mapping, [, outlineNode]) => {
        return {
          ...mapping,
          ...Object.fromEntries(outlineNode.map(node => [node.nodeKey, outlineNode])),
        };
      },
      {}
    );
    return outlineTextNodeToOutlineNodeMapping;
  },

  nodePath: (state, getters) => node => {
    return node.isRoot ? '' : getters.nodePath(node.parent) + ',' + node.nodeKey;
  },

  /**
   * Retrieve an object containing all the text nodes contained in a single outline node identified by the containing outline node id
   * {
   *  outline-node-id-1: [text nodes for outline node 1]
   *  ...
   *  outline-node-id-N: [text nodes for outline node N]
   * }
   */
  flattenOutlineTextNodes: (state, getters) => (outlineNode, index) => {
    let groundBaseTextNodes = {};
    if (outlineNode.groundedTextNodes?.length && outlineNode.basisNodes?.length === 0) {
      groundBaseTextNodes = outlineNode.groundedTextNodes.map(node => ({
        ...node,
        source: 'groundedTextNodes',
        index: index,
      }));
    } else if (outlineNode.basisNodes?.length) {
      groundBaseTextNodes = outlineNode.basisNodes.map(node => ({ ...node, source: 'basisNodes' }));
    } else {
      groundBaseTextNodes = outlineNode.nodes;
    }
    let outlineNodeTextNodeMapping = {
      [outlineNode._id]: groundBaseTextNodes,
    };
    if (outlineNode.sublevel?.length) {
      outlineNodeTextNodeMapping = outlineNode.sublevel.reduce(
        (acc, children) => ({
          ...acc,
          ...getters.flattenOutlineTextNodes(children),
        }),
        outlineNodeTextNodeMapping
      );
    }
    return outlineNodeTextNodeMapping;
  },

  /**
   * Object containing all the text nodes per outline node identified by the containing outline node id
   * {
   *  outline-node-id-1: [text nodes for outline node 1]
   *  ...
   *  outline-node-id-N: [text nodes for outline node N]
   * }
   */
  flatOutlineTextNodes: (state, getters) => {
    return getters.outline.reduce(
      (acc, outlineNode, index) => ({
        ...acc,
        ...getters.flattenOutlineTextNodes(outlineNode, index),
      }),
      {}
    );
  },

  /**
   * Object containing all annotations per text node identified by the containing text node id
   * {
   *  text-node-id-1: [annotations for text node 1]
   *  ...
   *  text-node-id-N: [annotations for text node N]
   * }
   */
  outlineAnnotations: (state, getters) => {
    const annotations = Object.entries(getters.flatOutlineTextNodes)?.reduce(
      (obj, [nodeId, textNodes]) => ({
        ...obj,
        [nodeId]: [
          ...new Set(
            textNodes
              ?.map(n => ({
                counterpoints:
                  n.source === 'groundedTextNodes'
                    ? getters.documentNodeLLMAnnotations[n.nodeKey]?.counterpoints
                    : getters.documentNodeAnnotations[n.nodeKey]?.counterpoints,
              }))
              ?.filter(n => n.counterpoints?.length)
              ?.flatMap(n => n.counterpoints?.flatMap(cp => cp.annotations))
          ),
        ],
      }),
      {}
    );

    for (let i in annotations) {
      if (!annotations[i].length) {
        delete annotations[i];
      }
    }

    return annotations;
  },

  showingOutlinePanel: (state, getters) => {
    return (
      getters.documentSelectedSource.sourceType === DocumentType.OUTLINE ||
      ([DocumentType.ARTICLE, DocumentType.TEXT].includes(
        getters.documentSelectedSource.sourceType
      ) &&
      (getters.roundStatus === RoundStatus.EDITING_NLP_OUTLINE ||
        getters.roundStatus === RoundStatus.EDITING_OUTLINED_ANALYZED)
        ? state.mainPanelView === 'article'
        : state.mainPanelView === 'outline')
    );
  },

  outlineNodeStyle: (state, getters) => data => {
    return getters.useExcerpts
      ? {
          position: 'absolute',
          top: `${(data.row - 1) * (fixedRowHeight / 2)}px`,
          'min-width': '20vw',
        }
      : {};
  },
};

//
// mutations
const mutations = {
  setHoverOutlineNodes: (state, nodesArr) => (state.hoverOutlineNodes = nodesArr),
  setNodeId: (state, data) => (state.nodeId = data),
  setBasisEdited: (state, edited) => (state.basisEdited = edited),
};

//
// actions
const actions = {
  setSelectedOutlineNode: ({ commit }, node) => {
    commit('setSelectedItem', { type: 'outlineNode', item: node });
  },
  // ***
  loadOutlineTree: async ({ state, dispatch }) => {
    if (!state.documentData?.sources) return;

    const article = state.documentData.sources.find(source =>
      [DocumentType.ARTICLE, DocumentType.TEXT].includes(source.sourceType)
    );
    const outline = state.documentData.sources.find(
      source => source.sourceType === DocumentType.OUTLINE
    );
    if (!article || !outline) return;

    let outlineSourceData = (
      outline?.outline?.outline ? outline?.outline?.outline : outline.outline
    ).map(node => getOutlineNodeForDisplay(node));
    outlineSourceData.forEach(node =>
      dispatch('setupOutlineNode', { node, parentArticle: article })
    );

    // set Outline ref nodes
    const outlineNodesLevel_2 = outlineSourceData.flatMap(o => o.sublevel);
    article.nodes.forEach(node => {
      let outlineLevel_1_Node = outlineSourceData.find(o =>
        (o.basisNodes || o.nodes)?.some(n => n.nodeKey === node.nodeKey)
      );

      if (outlineLevel_1_Node) {
        node.outlineNodes = [outlineLevel_1_Node];

        let outlineLevel_2_Node = outlineLevel_1_Node.sublevel.find(sub =>
          (sub.basisNodes || sub.nodes)?.some(n => n.nodeKey === node.nodeKey)
        );

        if (outlineLevel_2_Node) {
          node.outlineNodes.push(outlineLevel_2_Node);
        }
      } else {
        let outlineLevel_2_Node = outlineNodesLevel_2.find(o =>
          (o.basisNodes || o.nodes)?.some(n => n.nodeKey === node.nodeKey)
        );

        if (outlineLevel_2_Node) {
          node.outlineNodes = [outlineLevel_2_Node];
        }
      }
    });
  },

  setupOutlineNode: ({ state, dispatch }, { node, parentArticle }) => {
    // Vue.set(node, 'paragraphId', parseInt(node.nodeKey.replace('Summary_P', '')));
    node.annotations = state.counterpointData?.counterpointsFlat?.filter(
      ann => getId(ann.refOutlineNode) === getId(node)
    );
    node.children = node.sublevel;
    if (node.basisNodes || node.nodes) {
      (node.basisNodes || node.nodes).forEach(innerNode => {
        if (state.counterpointData?.rounds) {
          if (parentArticle) {
            let articleNode = parentArticle?.nodes?.find(n => n.nodeKey === innerNode.nodeKey);
            if (!articleNode?.counterpoints?.length) return;
            //
            // flatten counterpoint comments
            innerNode.counterpointAnnotationIDs = articleNode.counterpointAnnotationIDs;
            innerNode.counterpoints = articleNode.counterpoints;
          }
        }
      });
    }

    if (node.sublevel)
      node.sublevel.forEach(subNode =>
        dispatch('setupOutlineNode', { node: subNode, parentArticle })
      );
  },

  updateOutline: async ({ dispatch }, { outlineSource, outlineNodes, successFn, failedFn }) => {
    try {
      const outlineToSave = outlineNodes.map(node => getOutlineNodeForTransfer(node));
      const sourceToSave = {
        ...outlineSource,
        outline: outlineToSave,
      };

      // ***
      let res = await dispatch('updateSourceDocument', {
        documentSourceData: sourceToSave,
        documentSourceId: outlineSource._id,
      });
      // }
      if (res)
        if (successFn) successFn(res);
        else if (failedFn) failedFn(res);
      return res;
    } catch (err) {
      console.log(err);
      if (failedFn) failedFn(err);
    }
  },
  outlineSourceDocument: async ({ state }, { generateOutlineData, documentSourceId }) => {
    try {
      let res = await xhr.outlineSourceDocument(
        documentSourceId || getId(state.documentData.selectedSource),
        generateOutlineData
      );
      const docResult = res;

      return docResult;
    } catch (err) {
      console.log(err);
      return null;
    }
  },
};

//
// exports
export default {
  // namespaced: false,
  state,
  getters,
  actions,
  mutations,
};
